// frontend/src/utils/formatAge.ts

/**
 * Format the age of livestock from acquisition date.
 * Returns human-readable string like "3 months" or "1y 2m"
 *
 * @param acquisitionDate - When the livestock was acquired
 * @param endDate - Optional end date (e.g., deceased_date). If provided, calculates age at that date instead of now.
 */
export function formatAge(acquisitionDate: string | Date, endDate?: string | Date | null): string {
  const start = new Date(acquisitionDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'Future'; // Acquisition date in future
  }

  if (diffDays === 0) {
    return 'Today';
  }

  if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  }

  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks !== 1 ? 's' : ''}`;
  }

  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months !== 1 ? 's' : ''}`;
  }

  const years = Math.floor(diffDays / 365);
  const remainingMonths = Math.floor((diffDays % 365) / 30);

  if (remainingMonths === 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  }

  return `${years}y ${remainingMonths}m`;
}
